/**
 * The adapter between the Modern ledger's line-item view and the payload shape
 * the billing API actually accepts.
 *
 * The screen shows one row per charge line. The backend stores revision
 * SNAPSHOTS: named scalar keys plus two arrays keyed {Id, NameEn, Charge}. This
 * file is the only place that translation happens, and the payload builders
 * below reproduce BillingAdmin.jsx byte-for-byte — same keys, same defaults,
 * same deletes, same FormData serialisation.
 *
 * Two semantics that must never merge:
 *
 *   ADD    → POST charges/add/{SHIPMENT id}.    Values are DELTAS the backend
 *            adds to the previous revision. Payload is SPARSE: only what was
 *            typed is sent. Untouched fields are ABSENT, not 0.
 *   MODIFY → POST charges/update/{CHARGE ROW id}. Values are ABSOLUTES that
 *            replace. Payload is SEEDED: nine keys default to 0, the server's
 *            whole total_updated_charges spreads over them, edits spread over
 *            that, and invoice_id is deleted.
 *
 * Note the URL parameter is called `charge_id` in both service functions but
 * means a different entity in each.
 */

import { addCharge, updateInvoice } from "../../../services/admin.services";

/** Fixed charge keys, in the order the ledger renders them. The `name` IS the
 *  payload key — that is the contract with the backend. */
export const TRANSPORT_LINES = [
  { key: "base_price", label: "Base rate" },
  { key: "fuel_charge", label: "Fuel surcharge" },
  { key: "freight_charge", label: "Freight charge" },
];

export const ACCESSORIAL_LINES = [
  { key: "signature_charge", label: "Signature" },
  { key: "residential_charge", label: "Residential delivery" },
  { key: "PickupCharge", label: "Pickup charge" },
];

/** Only present when the shipment carries insurance — production appends this
 *  key conditionally and BillingTable hides the column otherwise. */
export const INSURANCE_LINE = {
  key: "insurance_charge",
  label: "Insurance charge",
};

/** The EDITABLE set — exactly the inputs production renders. Do not add to
 *  this: a new entry here becomes a new key in the sparse Add payload. */
export const fixedLinesFor = (data) => [
  ...TRANSPORT_LINES,
  ...ACCESSORIAL_LINES,
  ...(data?.insurance ? [INSURANCE_LINE] : []),
];

/** tailgate_charge is stored and billed but production's table renders no
 *  column for it, so a real charge disappears — on shipment 21740 it is 50.00
 *  against a tailgate_delivery:true shipment. The flat ledger shows it, because
 *  otherwise that 50 is silently absorbed into the derived base line. It stays
 *  DISPLAY-ONLY: Modify already carries it through the total_updated_charges
 *  spread, and giving Add an input would put a key on the wire that production
 *  never sends. */
export const TAILGATE_LINE = { key: "tailgate_charge", label: "Tailgate" };

/** Which keys belong to the Transportation group rather than Accessorials. */
export const TRANSPORT_KEYS = new Set(TRANSPORT_LINES.map((l) => l.key));

export const displayLinesFor = (data) => [
  ...fixedLinesFor(data),
  TAILGATE_LINE,
];

/** Dedupe by NameEn across every revision — the same derivation BillingAdmin
 *  uses to build its dynamic columns. */
export const uniqueCharges = (rows, group) =>
  (rows || [])
    .flatMap((row) => row?.[group] || [])
    .filter(
      (charge, index, self) =>
        charge && index === self.findIndex((c) => c?.NameEn === charge?.NameEn),
    );

export const latestOf = (rows) => (rows || [])[(rows || []).length - 1] || null;

export const findByName = (row, group, nameEn) =>
  (row?.[group] || []).find((c) => c?.NameEn === nameEn) || null;

/** Next Id for a client-invented tax / additional charge: max + 1 over the
 *  catalogue, matching NewTax.jsx and AdditionalCharge.jsx. */
export const nextChargeId = (catalogue) =>
  (catalogue || []).reduce((max, c) => (c?.Id > max ? c.Id : max), 0) + 1;

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Round to cents. Display only — no rounded value is ever sent. */
const r2 = (value) => Math.round(num(value) * 100) / 100;

/* ── Modify rows ───────────────────────────────────────────────────────
   After a save, the ledger appends the server's response row to
   shipment_charges, exactly as BillingAdmin does for BOTH endpoints
   (BillingAdmin.jsx:216-222, 294-300). For an Add that row is a delta. For a
   Modify it holds the ABSOLUTES the form sent — the accumulated add-state it
   replaces — so summing it like an addition inflates every line it carries.

   What the frontend can tell about charges/update, since nothing here reads
   the response beyond `total`:
     - it is posted against the LATEST row's id, seeded from that row's
       total_updated_charges (BillingAdmin.jsx:463-497);
     - before charges/add existed (June 2023) every save went through it, and
       the invoice struck through every row but the last — each save was a new
       snapshot superseding the one before (git 5f1c649d, EditableInvoice);
     - since then, Classic's customer invoice strikes a row out when the NEXT
       row's `type` is anything but "add" (BillingUser.jsx:107-110, and every
       column after it);
     - the Hajex admin API documents customer charge rows as ADDED, NEVER
       REWRITTEN, with `type` one of "add" / "update" / "delete" (a filename
       for an imported carrier bill). So a Modify appends a new row typed
       "update".
   Only `type === "update"` is read as a Modify — deliberately narrower than
   BillingUser's `!== "add"`, so a shipment with no update row (or an
   unexpected type value) keeps exactly the figures it had before. A row this
   session knows came back from charges/update counts too, and a repeated id
   still collapses in place, defensively, though the API says it never
   happens. Display only — nothing here reaches a payload, and
   `latest`, `accumulated` and the catalogues below are still read from the
   raw list, so what Modify and Add send is unchanged. */

/** Response rows from a Modify in this session. A WeakSet keyed by the row
 *  object itself: it tags nothing, so setData / setShipments receive exactly
 *  the object production appends, and a re-fetch starts clean. */
const modifyResults = new WeakSet();

export const markModifyResult = (row) => {
  if (row && typeof row === "object") modifyResults.add(row);
};

/** shipment_charges with a repeated id collapsed onto its first position —
 *  the later row wins, as it would in a re-fetch after an in-place update.
 *  Rows without an id are never merged. With unique ids (every shipment that
 *  never had a Modify) this returns the same rows in the same order. */
export const effectiveRevisions = (rows) => {
  const list = [];
  const replaced = new Set();
  const positionOf = new Map();
  (rows || []).forEach((row) => {
    const id = row?.id;
    if (id != null && positionOf.has(id)) {
      list[positionOf.get(id)] = row;
      replaced.add(row);
      return;
    }
    if (id != null) positionOf.set(id, list.length);
    list.push(row);
  });
  return { list, replaced };
};

/** Does this row hold absolutes that replace the state before it? The base
 *  (index 0) never does: it has nothing before it and is read as absolute
 *  already. Only the server's explicit "update" marks a Modify; "add",
 *  "delete", a carrier-bill filename, a missing field or anything else keeps
 *  the row an ordinary addition, as it was read before this existed. */
const isModifyRow = (row, index, replaced) =>
  index > 0 &&
  (replaced.has(row) || modifyResults.has(row) || row?.type === "update");


/**
 * How a shipment's charges are actually shaped — verified against a live
 * payload, because it is not what the column names suggest:
 *
 *   Revision 1 is the BASE INVOICE. Its component columns (fuel_charge etc.)
 *   are already INSIDE sub_total, and sub_total is the quote's sub_total times
 *   the markup. sub_total + tax = that revision's total.
 *
 *   Revisions 2..n are pure ADDITIONS. Only the column that changed carries a
 *   value, sub_total is 0, there are no tax lines, and `total` is the new
 *   running total. An addition therefore bypasses markup AND tax — it lands on
 *   the total at face value.
 *
 *   Shipment Total = revision 1's total + every later revision's delta.
 *
 *   A Modify row is the exception: it carries ABSOLUTES for the accumulated
 *   add-state it replaced (the previous row's total_updated_charges, which is
 *   exactly what the form was seeded from). What it changed is therefore its
 *   value minus that state, line by line — i.e. precisely what the admin
 *   edited. See "Modify rows" above.
 *
 * Reading the LAST revision's columns as "the current amounts" is wrong: it
 * shows only the most recent delta. Everything below is built from that.
 */
export const buildLineRows = (data) => {
  const revisions = data?.shipment_charges || [];
  // Figures are derived from the effective list; everything a payload is
  // built from (`latest`, `accumulated`, the catalogues) stays on the raw one.
  const { list: effective, replaced } = effectiveRevisions(revisions);
  const base = effective[0] || null;
  const later = effective.slice(1);
  const latest = latestOf(revisions);
  const latestQuote = latestOf(data?.quoteCharges);

  const additionalCatalogue = uniqueCharges(revisions, "additional_charges");
  const taxCatalogue = uniqueCharges(revisions, "tax_charges");
  const lines = fixedLinesFor(data);
  const displayLines = displayLinesFor(data);

  /** Component lines of the base invoice, with the matching quote line. */
  const baseComponents = lines.map(({ key, label }) => ({
    kind: "fixed",
    key,
    label,
    billed: num(base?.[key]) || null,
    quote: num(latestQuote?.[key]) || null,
  }));

  const baseAdditional = additionalCatalogue.map((charge) => ({
    kind: "additional",
    key: charge?.NameEn,
    label: charge?.NameEn,
    meta: charge,
    billed: findByName(base, "additional_charges", charge?.NameEn)?.Charge ?? null,
    quote:
      findByName(latestQuote, "additional_charges", charge?.NameEn)?.Charge ??
      null,
  }));

  const baseTaxes = (base?.tax_charges || []).map((tax) => ({
    kind: "tax",
    key: tax?.NameEn,
    label: tax?.NameEn,
    meta: tax,
    billed: tax?.Charge ?? null,
    quote: findByName(latestQuote, "tax_charges", tax?.NameEn)?.Charge ?? null,
  }));

  const sumTax = (row) =>
    (row?.tax_charges || []).reduce((total, t) => total + num(t?.Charge), 0);

  /** What a Modify row changed: its absolutes minus the state it replaced,
   *  per line, keeping only lines that moved. The replaced state is the
   *  previous row's total_updated_charges — the form's seed — or, for a row
   *  that has none (legacy), that row itself. Null for an ordinary addition,
   *  which keeps its own columns as its delta, exactly as before. */
  const modifyChangeOf = (row, index) => {
    if (!isModifyRow(row, index + 1, replaced)) return null;
    const previous = effective[index];
    const before =
      previous?.total_updated_charges &&
      typeof previous.total_updated_charges === "object"
        ? previous.total_updated_charges
        : previous;
    const namedDelta = (group) => {
      const names = [];
      [...(row?.[group] || []), ...(before?.[group] || [])].forEach((c) => {
        if (c?.NameEn && !names.includes(c.NameEn)) names.push(c.NameEn);
      });
      return names
        .map((name) => ({
          key: name,
          label: name,
          amount:
            num(findByName(row, group, name)?.Charge) -
            num(findByName(before, group, name)?.Charge),
        }))
        .filter((p) => Math.abs(p.amount) >= 0.005);
    };
    return {
      fixed: displayLines
        .map(({ key, label }) => ({
          key,
          label,
          amount: num(row?.[key]) - num(before?.[key]),
        }))
        .filter((p) => Math.abs(p.amount) >= 0.005),
      additional: namedDelta("additional_charges"),
      taxes: namedDelta("tax_charges"),
    };
  };
  const modifyChanges = later.map(modifyChangeOf);
  const hasModify =
    replaced.size > 0 || effective.some((row) => modifyResults.has(row)) ||
    modifyChanges.some(Boolean);

  /** One entry per later revision: what it added, why, and the running total. */
  const adjustments = later.map((row, index) => {
    const additionParts = () => [
      ...lines
        .filter(({ key }) => num(row?.[key]) !== 0)
        .map(({ key, label }) => ({ key, label, amount: num(row[key]) })),
      ...(row?.additional_charges || [])
        .filter((c) => num(c?.Charge) !== 0)
        .map((c) => ({ key: c?.NameEn, label: c?.NameEn, amount: num(c.Charge) })),
      ...(row?.tax_charges || [])
        .filter((t) => num(t?.Charge) !== 0)
        .map((t) => ({ key: t?.NameEn, label: t?.NameEn, amount: num(t.Charge) })),
    ];
    const change = modifyChanges[index];
    const parts = change
      ? [
          ...change.fixed.filter(({ key }) => lines.some((l) => l.key === key)),
          ...change.additional,
          ...change.taxes,
        ]
      : additionParts();

    const previousTotal = num(later[index - 1]?.total ?? base?.total);

    return {
      row,
      revision: index + 2, // revision 1 is the base
      modify: Boolean(change),
      parts,
      // trust the running total for the amount — it is the server's figure and
      // survives a revision whose components we cannot itemise
      amount: num(row?.total) - previousTotal,
      partsAmount: parts.reduce((total, p) => total + p.amount, 0),
      description: row?.description || null,
      createdAt: row?.created_at || null,
      total: num(row?.total),
      media: row?.media || [],
    };
  });

  const baseSubTotal = num(base?.sub_total);
  const quoteSubTotal = num(latestQuote?.sub_total);

  /* ── One flat ledger ───────────────────────────────────────────────────
     A post-booking charge is still a charge. Listing it inline with its
     provenance, instead of in a separate block at the foot, lets the whole
     invoice read top to bottom — charges, subtotal, tax, total — and it still
     reconciles exactly:

       derived base 186.91 + fuel 124.02 + service 77.73        = 388.66
       + pickup 2.52 + residential 20.00 + base rate 2.00       = 413.18
       + GST 19.42                                              = 432.60  ✓

     The derived base is the part of the pre-markup subtotal that sits in no
     named column. It is a subtraction, never a stored figure, so it is
     flagged and must always render as derived. */
  const serviceCharge = num(data?.service_charge);
  /* Every itemised line on the base revision — fixed columns AND named
     additional charges. Omitting the additional charges overstates the derived
     base by exactly their sum: on production shipment 22056 the Processing Fee
     of 0.91 pushed the charge list to 32.01 against a 31.10 subtotal. */
  const itemisedBase =
    displayLines.reduce((total, { key }) => total + num(base?.[key]), 0) +
    (base?.additional_charges || []).reduce(
      (total, c) => total + num(c?.Charge),
      0,
    );
  /* Derive from the BILLED row, never the pre-markup one. The pre-markup row's
     sub_total is not reliable: on a shipment with no markup it is 0 while its
     total is the full amount, which made this come out negative and silently
     drop the line — leaving a charge list that summed to 25.66 against a
     144.95 subtotal. Billed sub − service − itemised holds in both cases. */
  const derivedBase = r2(baseSubTotal - serviceCharge - itemisedBase);

  /* The same subtraction on the pre-markup row. It is NOT the billed figure:
     on shipment 22056 the billed side has a Processing Fee the quote row lacks,
     so the two remainders are 8.84 and 2.68. Reusing one for both columns
     would put a billed number in a carrier-side column. */
  const itemisedQuote =
    displayLines.reduce((total, { key }) => total + num(latestQuote?.[key]), 0) +
    (latestQuote?.additional_charges || []).reduce(
      (total, c) => total + num(c?.Charge),
      0,
    );
  const derivedQuote = quoteSubTotal ? r2(quoteSubTotal - itemisedQuote) : 0;

  /** Which revisions touched a given line, so provenance can sit on the row. */
  const chargeOrigins = {};
  const taxOrigins = {};
  const pushOrigin = (bag, name, entry) => {
    if (!name) return;
    if (!bag[name]) bag[name] = [];
    bag[name].push(entry);
  };

  later.forEach((row, index) => {
    const entry = {
      revision: index + 2,
      createdAt: row?.created_at || null,
      description: row?.description || null,
    };
    const change = modifyChanges[index];
    if (change) {
      // a Modify contributes what it CHANGED — signed, and only where a line
      // moved — so the line lands on the absolute the admin typed
      const modifyEntry = { ...entry, modify: true };
      change.fixed.forEach((p) =>
        pushOrigin(chargeOrigins, p.key, { ...modifyEntry, amount: p.amount }),
      );
      change.additional.forEach((p) =>
        pushOrigin(chargeOrigins, p.key, { ...modifyEntry, amount: p.amount }),
      );
      change.taxes.forEach((p) =>
        pushOrigin(taxOrigins, p.key, { ...modifyEntry, amount: p.amount }),
      );
      return;
    }
    displayLines.forEach(({ key }) => {
      if (num(row?.[key]) !== 0)
        pushOrigin(chargeOrigins, key, { ...entry, amount: num(row[key]) });
    });
    (row?.additional_charges || []).forEach((c) => {
      if (num(c?.Charge) !== 0)
        pushOrigin(chargeOrigins, c?.NameEn, {
          ...entry,
          amount: num(c.Charge),
        });
    });
    // a tax can be added after booking too — it belongs with the taxes, not
    // with the freight lines
    (row?.tax_charges || []).forEach((t) => {
      if (num(t?.Charge) !== 0)
        pushOrigin(taxOrigins, t?.NameEn, { ...entry, amount: num(t.Charge) });
    });
  });

  const lineFor = (key, label, group, baseValue, quoteValue) => {
    const origins = chargeOrigins[key] || [];
    const added = origins.reduce((total, o) => total + o.amount, 0);
    return {
      key,
      label,
      group,
      baseValue: r2(baseValue),
      added: r2(added),
      amount: r2(baseValue + added),
      // the matching line on the pre-markup row, so the comparison column
      // shows real figures instead of a dash beside every charge
      quote: r2(quoteValue),
      origins,
      derived: false,
    };
  };

  const flatCharges = [
    ...(derivedBase > 0
      ? [
          {
            key: "__derived_base",
            label: "Not itemised",
            group: "transport",
            amount: derivedBase,
            baseValue: derivedBase,
            quote: derivedQuote,
            added: 0,
            origins: [],
            derived: true,
          },
        ]
      : []),
    ...displayLines.map(({ key, label }) =>
      lineFor(
        key,
        label,
        TRANSPORT_KEYS.has(key) ? "transport" : "accessorial",
        num(base?.[key]),
        num(latestQuote?.[key]),
      ),
    ),
    ...additionalCatalogue.map((charge) =>
      lineFor(
        charge?.NameEn,
        charge?.NameEn,
        "accessorial",
        num(findByName(base, "additional_charges", charge?.NameEn)?.Charge),
        num(findByName(latestQuote, "additional_charges", charge?.NameEn)?.Charge),
      ),
    ),
  ];

  const flatTaxes = [
    ...uniqueCharges(revisions, "tax_charges").map((tax) => {
      const name = tax?.NameEn;
      const origins = taxOrigins[name] || [];
      const baseValue = num(findByName(base, "tax_charges", name)?.Charge);
      const added = origins.reduce((total, o) => total + o.amount, 0);
      return {
        key: name,
        label: name,
        baseValue: r2(baseValue),
        added: r2(added),
        amount: r2(baseValue + added),
        quote: r2(num(findByName(latestQuote, "tax_charges", name)?.Charge)),
        origins,
      };
    }),
  ];

  /* ── Carrier cost ──────────────────────────────────────────────────────
     Charges carrying an invoice_id come from the carrier's own billing feed
     (production shipment 22056 posts one via an EDI file, invoice 63502261).
     Where that has landed, cost and margin are REAL — not derived — so the
     metric strip can finally show them. Where it has not, they stay absent
     rather than being inferred from the pre-markup row. */
  const carrierRows = data?.billingAutomation || [];
  const carrierTotal = r2(
    carrierRows.reduce((total, row) => total + num(row?.total), 0),
  );
  const carrierInvoiceNumbers = Array.from(
    new Set(carrierRows.map((row) => row?.invoice_id).filter(Boolean)),
  );
  const hasCarrierBill = carrierRows.length > 0 && carrierTotal > 0;
  const shipmentTotal = num(data?.Total);
  const margin = hasCarrierBill ? r2(shipmentTotal - carrierTotal) : null;

  // everything added after booking that is NOT a tax lands before the subtotal
  const nonTaxAdded = flatCharges.reduce((total, l) => total + l.added, 0);
  const flatTaxTotal = flatTaxes.reduce((total, t) => total + t.amount, 0);

  /* After a Modify the stored total is whatever the admin typed into the
     Total box — Modify replaces it outright and nothing recomputes it from the
     lines (BillingTable.jsx:403-411). So the lines are not guaranteed to add up
     to it, and a billing screen must not print a column that silently fails
     to. The subtotal is taken from the server's Total less the taxes, and any
     part of it no line accounts for is shown as its own DERIVED line — a
     subtraction, flagged, never a stored figure, like "Not itemised" above.
     When the admin kept the total in step with the lines it is zero and does
     not render. A shipment that never had a Modify takes the first branch and
     is untouched. */
  const anchorToTotal =
    hasModify &&
    data?.Total != null &&
    data?.Total !== "" &&
    !Number.isNaN(parseFloat(data.Total));
  const flatSubtotal = anchorToTotal
    ? r2(num(data.Total) - flatTaxTotal)
    : r2(baseSubTotal + nonTaxAdded);
  const unitemisedByModify = anchorToTotal
    ? r2(
        flatSubtotal -
          (serviceCharge > 0 ? serviceCharge : 0) -
          flatCharges.reduce((total, l) => total + l.amount, 0),
      )
    : 0;
  const ledgerCharges =
    Math.abs(unitemisedByModify) >= 0.005
      ? [
          ...flatCharges,
          {
            key: "__modify_unitemised",
            label: "Not itemised by Modify",
            group: "transport",
            amount: unitemisedByModify,
            baseValue: 0,
            quote: null,
            added: 0,
            origins: [],
            derived: true,
            note: "the total a Modify stored, less the lines and taxes above — no charge column carries it",
          },
        ]
      : flatCharges;

  /**
   * The reference figure the editor shows beside each line.
   *
   * Read from the server's OWN accumulated object — the last revision's
   * `total_updated_charges` — which is exactly what production seeds its edit
   * form from. Summing the raw per-revision columns instead looks equivalent
   * while every revision is an Add, but a Modify writes ABSOLUTE replacement
   * values into its row, so the sum double-counts everything before it and the
   * admin types a delta against an overstated number.
   *
   * Taxes and accessorials are kept in SEPARATE maps: merging them by NameEn
   * made a name that exists as both show the sum on both rows.
   */
  const accumulated = latest?.total_updated_charges || null;

  const currentByKey = {};
  displayLines.forEach(({ key }) => {
    currentByKey[key] = num(accumulated?.[key]);
  });

  const currentNamed = {};
  additionalCatalogue.forEach((entry) => {
    if (entry?.NameEn)
      currentNamed[entry.NameEn] = num(
        findByName(accumulated, "additional_charges", entry.NameEn)?.Charge,
      );
  });

  const currentTaxes = {};
  taxCatalogue.forEach((entry) => {
    if (entry?.NameEn)
      currentTaxes[entry.NameEn] = num(
        findByName(accumulated, "tax_charges", entry.NameEn)?.Charge,
      );
  });

  return {
    // ── flat ledger: charges (incl. post-booking), service, subtotal, tax ──
    flatCharges: ledgerCharges,
    flatTaxes,
    derivedBase,
    derivedQuote,
    flatSubtotal,
    flatTaxTotal,
    nonTaxAdded,
    hasCarrierBill,
    carrierTotal,
    carrierInvoiceNumbers,
    margin,
    currentByKey,
    currentNamed,
    currentTaxes,
    revisions,
    // the list the figures are derived from: a Modify echo that reuses an id
    // has replaced the row it updated, as a re-fetch would show it
    effectiveRevisions: effective,
    hasModify,
    unitemisedByModify,
    base,
    later,
    latest,
    latestQuote,
    adjustments,
    baseComponents,
    baseAdditional,
    baseTaxes,
    additionalCatalogue,
    taxCatalogue,
    baseSubTotal: baseSubTotal || null,
    baseTax: sumTax(base) || null,
    baseTotal: num(base?.total) || null,
    quoteSubTotal: quoteSubTotal || null,
    quoteTax: sumTax(latestQuote) || null,
    quoteTotal: num(latestQuote?.total) || null,
    /* The pre-markup column is the one place on this screen where a stored
       figure and the lines above it can disagree. On production 22056 the row
       stores sub_total 13.32, TPS 0.97 and total 20.45 — its own parts add to
       14.29, a 6.16 gap, and it is the 20.45 that tracks the carrier's real
       bill of 20.83, so neither number can simply be thrown away. Rather than
       silently print a column that does not add up on a billing screen, expose
       both and let the ledger say so. The billed column is untouched by this:
       it reconciles on every shipment checked. */
    quoteColumnSum: r2(quoteSubTotal + sumTax(latestQuote)) || null,
    adjustmentsTotal: adjustments.reduce((total, a) => total + a.amount, 0),
    // service_charge is a shipment-level server field. Display only; it
    // appears in no payload anywhere in the invoice.
    serviceCharge,
    markup: num(data?.markup),
    total: num(data?.Total),
    /** What Modify edits: the accumulated add-state the endpoint replaces. */
    accumulated,
  };
};

/**
 * Exact copy of BillingAdmin.jsx's serialiser. Do not "improve" it: the
 * bracketed array/object key shapes are what the backend parses.
 */
const convertPayloadToFormData = (payload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (key === "file") {
      if (Array.isArray(value)) {
        value.forEach((file, index) => formData.append(`file[${index}]`, file));
      } else if (value instanceof File) {
        formData.append("file[0]", value);
      }
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        Object.entries(item).forEach(([subKey, subValue]) => {
          formData.append(`${key}[${index}][${subKey}]`, subValue);
        });
      });
    } else if (typeof value === "object" && value !== null) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        formData.append(`${key}[${subKey}]`, subValue);
      });
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

/**
 * Truthiness filter, copied from production verbatim. A line cleared to ""
 * is DROPPED from the array rather than sent as zero — a different
 * instruction to the backend. Note "0" is a truthy STRING, so an explicit
 * zero does survive and is sent as Charge=0.
 */
const collectNamed = (catalogue, edits) =>
  (catalogue || [])
    .filter((entry) => edits[entry.NameEn])
    .map((entry) => ({ ...entry, Charge: edits[entry.NameEn] }));

/**
 * Verbatim copy of createChargeObject (BillingAdmin.jsx:82-85). No guard, on
 * purpose: see seedModifyTaxEdits.
 */
const createChargeObject = (inputArray) =>
  Object.fromEntries(inputArray.map(({ NameEn, Charge }) => [NameEn, Charge]));

/**
 * The Modify tax seed, exactly as production builds it (BillingAdmin.jsx:
 * 488-497): the accumulated tax_charges map with the accumulated
 * additional_charges map spread OVER it. Classic bugs reproduced on purpose —
 * matches Classic BillingAdmin.jsx:476-497, see COVERAGE.md:
 *
 *   - a NameEn present as both a tax and an accessorial is seeded with the
 *     ACCESSORIAL's amount, and an accessorial whose name only exists as an
 *     older revision's tax brings that tax back;
 *   - an entry with no NameEn is keyed "undefined", so a catalogue tax with no
 *     name is re-sent when its Charge is truthy;
 *   - if either array is missing/null (or holds a null entry), production's
 *     createChargeObject throws inside the Modify click, after modify mode is
 *     already set but BEFORE setNewTaxData runs. newTaxData stays {} (it is
 *     always {} when Modify can be clicked), the tax inputs render blank and
 *     Save sends no tax_charges. The catch reproduces that end state.
 */
export const seedModifyTaxEdits = (accumulated) => {
  try {
    return {
      ...createChargeObject(accumulated?.tax_charges),
      ...createChargeObject(accumulated?.additional_charges),
    };
  } catch (error) {
    return {};
  }
};

/**
 * ADD — deltas, sparse payload, SHIPMENT id.
 * Mirrors handleSaveCharge (BillingAdmin.jsx:186-209).
 */
export const submitAddCharge = ({
  shipmentId,
  fixedEdits,
  taxEdits,
  additionalEdits,
  taxCatalogue,
  additionalCatalogue,
  files,
}) => {
  /* Spread VERBATIM. `fixedEdits` starts {} in Add mode and only gains keys the
     admin touched, which is already the sparse contract. Filtering empties on
     top of that diverged from production: classic writes every keystroke onto
     its bag unconditionally, so a field typed into and then cleared goes out as
     `key=` (present, empty). Dropping it changed the request body.

     The note lives in that same bag, as in production (the textarea writes
     newChargeData.description, BillingAdmin.jsx:551-556). So `description` sits
     at its FIRST-TOUCH position among the fixed keys, goes out as
     `description=` when typed then cleared, and is absent when never touched —
     matches Classic BillingAdmin.jsx:201-207, see COVERAGE.md. */
  const newChargeData = { ...(fixedEdits || {}) };

  return addCharge(
    convertPayloadToFormData({
      ...newChargeData,
      file: files,
      tax_charges: collectNamed(taxCatalogue, taxEdits || {}),
      additional_charges: collectNamed(
        additionalCatalogue,
        additionalEdits || {},
      ),
    }),
    shipmentId,
  );
};

/**
 * MODIFY — absolutes, seeded payload, CHARGE ROW id.
 * Mirrors handleUpdateCharge (BillingAdmin.jsx:246-286).
 *
 * The nine zero-defaults are load-bearing: any component the server did not
 * return inside total_updated_charges goes out as literal 0. insurance_charge
 * is NOT among them, so an insured shipment depends entirely on the spread of
 * total_updated_charges to carry it — which is why the whole server object is
 * spread rather than a diff.
 *
 * additional_charges is NOT recomputed here. It rides along inside
 * total_updated_charges exactly as production sends it; the ledger renders
 * those rows read-only in modify mode so the UI cannot imply otherwise.
 */
export const submitModifyCharge = ({
  chargeRowId,
  totalUpdatedCharges,
  fixedEdits,
  taxEdits,
  taxCatalogue,
  files,
}) => {
  /* Edits spread VERBATIM — no sparse filter. That filter belongs to Add
     alone. Production seeds newChargeData from total_updated_charges and
     writes every keystroke onto it unconditionally (BillingAdmin.jsx:93),
     then spreads it whole, so a cleared field reaches the endpoint as "".
     Filtering here made a cleared line silently re-send its stored amount —
     the opposite of what the screen promises.

     The note rides in `fixedEdits` too, and only ever holds what the server
     returned or what the admin typed (BillingAdmin.jsx:472-475, 613-618).
     Untouched, total_updated_charges' own value is sent as is — a null
     serialises as the string "null" — and an absent key falls back to the
     description: "" default below. Matches Classic BillingAdmin.jsx:267-279,
     see COVERAGE.md. */
  const newChargeData = {
    ...(totalUpdatedCharges || {}),
    ...(fixedEdits || {}),
  };

  const payload = {
    base_price: 0,
    fuel_charge: 0,
    freight_charge: 0,
    sub_total: 0,
    PickupCharge: 0,
    residential_charge: 0,
    signature_charge: 0,
    description: "",
    additional_price: 0,
    file: files,
    ...newChargeData,
  };

  // Load-bearing: sending invoice_id back re-associates the charge with a
  // carrier invoice.
  delete payload.invoice_id;

  return updateInvoice(
    convertPayloadToFormData({
      ...payload,
      tax_charges: collectNamed(taxCatalogue, taxEdits || {}),
    }),
    chargeRowId,
  );
};

/**
 * Preview of what the new revision's total will be. DISPLAY ONLY — this number
 * is never sent and never written into `Total`. The server returns the real
 * total and that is what the modal and the admin grid show after a save.
 */
export const previewDelta = ({
  mode,
  rows,
  fixedEdits,
  taxEdits,
  additionalEdits,
}) => {
  const sum = (edits, skip = []) =>
    Object.entries(edits || {})
      .filter(([key]) => !skip.includes(key))
      .reduce((acc, [, value]) => acc + num(value), 0);

  if (mode === "add") {
    /* Count sub_total: the Add form renders an input for it and
       submitAddCharge sends it, so excluding it showed a frozen projection
       while the server total moved. Production counts it too — its
       calculateTotal skips only `description`, which lives in the same bag
       and is skipped here for the same reason. `total` is excluded because
       Add has no total input. */
    const entered =
      sum(fixedEdits, ["total", "description"]) +
      sum(taxEdits) +
      sum(additionalEdits);
    return { delta: entered, projected: rows.total + entered };
  }

  // Modify replaces the accumulated state including the grand total, which the
  // admin edits directly.
  const nextTotal = fixedEdits?.total;
  if (nextTotal == null || nextTotal === "") {
    return { delta: null, projected: null };
  }
  return { delta: num(nextTotal) - rows.total, projected: num(nextTotal) };
};
